import { isPaidStatus, isInvoicePayable, keptTotal, invoiceDisplayStatus } from '../../utils/invoiceOverdue';   // Block H-5: refunds
import { useState, useMemo, useEffect, useCallback } from "react";
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { describeWriteFailure } from '../../utils/writeFailure';   // a failed write says so, where the control is
import { messageFromError } from '../../utils/apiError';
import { formatMoney } from '../../utils/currency';
import { getProjectById, getProjects } from "../../services/projectService";
import { useInvoices } from "../../contexts/InvoicesContext";
import { useExpenses } from "../../contexts/ExpensesContext";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import { api } from "../../api/client";
import {
  Button, StatTile, StatusPill, EmptyState, Skeleton, ErrorState, ConfirmDialog, ProgressBar, Card,
  useDelayedDelete,
} from '../../ui';
import ExpenseForm from "../Invoicing/ExpenseForm";
import SectionHead from '../Invoicing/SectionHead';
import './projectFinancials.css';

// Block K (UI-22): the per-project money view, on the v2 kit. It renders inside InvoicingLayout's
// page (and inside ProjectTracker's Finances tab), so it opens with a SectionHead, never a
// PageLayout or PageHeader of its own.

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Append noon only to a bare date so date-only values ("2026-08-12") don't shift a day across
// timezones; leave a full ISO timestamp (paidAt from the payment webhook) untouched, else
// "…000ZT12:00:00" → Invalid Date / NaN in the timeline math.
function toDate(str) {
  const s = String(str);
  return new Date(s.includes("T") ? s : s + "T12:00:00");
}

function fmtDate(str) {
  if (!str) return "—";
  const d = toDate(str);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function calcInvoiceTotal(lineItems = []) {
  const sub = (lineItems || []).reduce((s, r) => s + ((Number(r.qty) || 0) * (Number(r.unitPrice) || 0)), 0);
  const tax = (lineItems || []).reduce((s, r) => s + ((Number(r.qty) || 0) * (Number(r.unitPrice) || 0) * (Number(r.tax) || 0) / 100), 0);
  return sub + tax;
}

const currencyOf = (inv) => inv.currency || 'GBP';

// Decision 72: never add two currencies together. A sum is { GBP: 3465, EUR: 1200 }, shown as
// "£3,465.00 plus €1,200.00" with the account's own currency first.
function addTo(sums, code, amount) {
  sums[code] = (sums[code] || 0) + amount;
  return sums;
}
function moneyBy(sums, base) {
  const codes = Object.keys(sums).filter(c => sums[c] !== 0)
    .sort((a, b) => (a === base ? -1 : b === base ? 1 : a.localeCompare(b)));
  if (codes.length === 0) return formatMoney(0, base);
  return codes.map(c => formatMoney(sums[c], c)).join(' plus ');
}

// An invoice is "invoiced" once it has gone to the client: drafts are not sent yet and a
// cancelled invoice was voided (the Invoices tiles exclude both the same way).
const isInvoiced = (inv) => inv.status !== 'draft' && inv.status !== 'cancelled';

// ─── Status words ─────────────────────────────────────────────────────────────

// invoiceDisplayStatus is the one answer every invoice screen asks (Block H-5).
const INVOICE_STATUS = {
  paid: { tone: 'success', label: 'Paid' },
  pending: { tone: 'pending', label: 'Due' },   // fresh-eyes 24
  overdue: { tone: 'warning', label: 'Overdue' },
  draft: { tone: 'neutral', label: 'Draft' },
  cancelled: { tone: 'neutral', label: 'Cancelled' },
  partially_refunded: { tone: 'neutral', label: 'Partially refunded' },
  refunded: { tone: 'neutral', label: 'Refunded' },
};

function InvoiceStatus({ invoice }) {
  const st = INVOICE_STATUS[invoiceDisplayStatus(invoice)] || INVOICE_STATUS.pending;
  return <StatusPill tone={st.tone}>{st.label}</StatusPill>;
}

const PROJECT_TONE = { active: 'success', completed: 'neutral', paused: 'pending', archived: 'neutral' };

function ProjectStatus({ status }) {
  const s = status || 'active';
  return <StatusPill tone={PROJECT_TONE[s] || 'neutral'}>{s.charAt(0).toUpperCase() + s.slice(1)}</StatusPill>;
}

// ─── Timeline ─────────────────────────────────────────────────────────────────

const EVENT_KIND = {
  start: 'Project milestone',
  end: 'Project milestone',
  invoice: 'Invoice issued',
  paid: 'Payment received',
};

function ProjectTimeline({ events }) {
  if (events.length === 0) {
    return <p className="invc-muted">No dated events to display.</p>;
  }
  const sorted = [...events].sort((a, b) => toDate(a.date).getTime() - toDate(b.date).getTime());
  return (
    <ol className="pf-timeline" aria-label="Project financial timeline">
      {sorted.map((ev, i) => (
        <li key={i} className="pf-timeline__item">
          <span className={`pf-timeline__mark pf-timeline__mark--${ev.type}`} aria-hidden="true" />
          <span className="pf-timeline__date">{fmtDate(ev.date)}</span>
          <span className="pf-timeline__label">
            <span className="pf-timeline__name">{ev.label}</span>
            <span className="invc-small">{EVENT_KIND[ev.type]}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * ProjectFinancials
 *
 * Props:
 *   projectId     — numeric project id (required)
 *   onBack        — callback: close this view and return to caller
 *   onNewInvoice  — optional callback(projectId): opens invoice creation form
 *                   with this project pre-selected. If omitted, a fallback
 *                   message is shown.
 */
export default function ProjectFinancials({ projectId, onBack, onNewInvoice }) {
  const { invoices: allInvoices, setInvoices } = useInvoices();
  // `refresh` is deliberately NOT taken from these contexts any more. It was the old failure
  // handling — console.error then refetch — and a refetch is the one thing that cannot work
  // in the case that matters: with no network it fails too, leaving the invented row on
  // screen. Rolling back to the snapshot is correct whether or not the network is there.
  const { expenses: allExpenses, setExpenses } = useExpenses();
  const { currentUser } = useCurrentUser();
  // Expenses and the budget carry no currency of their own: they are in the account's currency.
  const base = currentUser?.baseCurrency || currentUser?.currencyDisplay || 'GBP';
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [markingId, setMarkingId] = useState(null);
  const [confirmCancelled, setConfirmCancelled] = useState(null);   // the cancelled invoice to mark paid

  // ── Data ──────────────────────────────────────────────────────────────────

  const [project, setProject] = useState(() => getProjectById(projectId));
  const [loading, setLoading] = useState(() => !getProjectById(projectId));
  const [loadError, setLoadError] = useState(null);

  const loadProject = useCallback(() => {
    let alive = true;
    setLoadError(null);
    if (!getProjectById(projectId)) setLoading(true);
    getProjects()
      .then(() => {
        if (alive) setProject(getProjectById(projectId));
      })
      .catch((err) => {
        if (!alive) return;
        const cached = getProjectById(projectId);
        setProject(cached);
        if (!cached) setLoadError(err);   // nothing to show: say the load failed, not "not found"
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  useEffect(() => loadProject(), [loadProject]);

  const invoices = useMemo(
    () => allInvoices.filter(i => String(i.project_id) === String(projectId)),
    [allInvoices, projectId],
  );

  // Decision 7: a deleted expense disappears at once, a toast offers Undo for 6 seconds, and
  // only then is the DELETE sent; if it fails, the expense comes back with the server's words.
  const del = useDelayedDelete({
    send: (exp, { keepalive }) => api(`/api/expenses/${exp.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (exp) => setExpenses(prev => prev.filter(e => e.id !== exp.id)),
    describe: (exp) => `Deleted ${exp.title || 'the expense'}.`,
    failMessage: 'That expense could not be deleted.',
  });

  const isHidden = del.isHidden;
  const expenses = useMemo(
    () => allExpenses.filter(e => String(e.project_id) === String(projectId) && !isHidden(e.id)),
    [allExpenses, projectId, isHidden],
  );

  // ── Financial calculations ────────────────────────────────────────────────

  const financials = useMemo(() => {
    const invoicedBy = {};
    const receivedBy = {};
    const outstandingBy = {};
    invoices.forEach(i => {
      const total = calcInvoiceTotal(i.lineItems);
      const code = currencyOf(i);
      if (isInvoiced(i)) addTo(invoicedBy, code, total);
      // Block H-5: paid, less anything refunded.
      if (isPaidStatus(i.status)) addTo(receivedBy, code, keptTotal(i, total));
      if (isInvoicePayable(i)) addTo(outstandingBy, code, total);
    });
    const totalExpenses = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    // Net profit is only honest in one currency: what was received in the account's currency
    // less the expenses (which are in it). Anything received in another currency is named
    // beside it, not converted (decision 72).
    const receivedBase = receivedBy[base] || 0;
    const otherReceived = Object.fromEntries(Object.entries(receivedBy).filter(([c, v]) => c !== base && v !== 0));
    return { invoicedBy, receivedBy, outstandingBy, totalExpenses, netProfit: receivedBase - totalExpenses, otherReceived };
  }, [invoices, expenses, base]);

  // ── Timeline events ───────────────────────────────────────────────────────

  const timelineEvents = useMemo(() => {
    const evts = [];
    if (project?.start_date) evts.push({ date: project.start_date, type: "start", label: "Project start" });
    invoices.forEach(inv => {
      if (inv.issueDate) evts.push({ date: inv.issueDate, type: "invoice", label: inv.number });
      if (isPaidStatus(inv.status) && inv.paidAt) evts.push({ date: inv.paidAt, type: "paid", label: `Paid: ${inv.number}` });
    });
    if (project?.end_date) evts.push({ date: project.end_date, type: "end", label: "Project end" });
    return evts;
  }, [project, invoices]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleMarkPaid(invoice) {
    // Block 171 (as on the Invoices list): moving a cancelled invoice to paid must be deliberate.
    if (invoice.status === 'cancelled') { setConfirmCancelled(invoice); return; }
    return markPaid(invoice);
  }

  async function markPaid(invoice) {
    // CANONICAL FORMAT: a FULL ISO TIMESTAMP, not a date.
    //
    // invoices.paid_at is TEXT and held both: this line wrote 10 characters while
    // lib/invoicePayments.js (the Stripe card path) wrote 24. lib/healthScore.js then compared the
    // two as instants, so a client who paid by card at 14:00 on the due date read as LATE while the
    // same invoice marked by hand read as on time — the clients who paid most conveniently scored
    // worst. The comparison is fixed in lib/invoiceDates.js; this stops NEW rows inheriting the
    // split, because a timestamp column is the kind of thing the next instrument gets built on.
    //
    // The timestamp wins because "when did they pay" is a real question and `slice(0, 10)` throws
    // away information that cannot be recovered. Nothing depends on date-only: every reader either
    // displays it (fmtDate and ukDate already handle both) or wants a real instant.
    const paidAt = new Date().toISOString();
    const original = invoice;
    // Block K: "Invoice marked as paid." used to show before the request was sent, and a failure
    // only reached the console. The row still turns paid at once, but the toast waits for the
    // server, and a failure puts the invoice back and says so.
    setMarkingId(invoice.id);
    setInvoices(prev => prev.map(i => i.id === invoice.id ? { ...i, status: "paid", paidAt } : i));
    try {
      await api(`/api/invoices/${invoice.id}`, { method: 'PUT', body: JSON.stringify({ status: 'paid', paidAt }) });
      showSuccessToast("Invoice marked as paid.");
    } catch (err) {
      setInvoices(prev => prev.map(i => i.id === original.id ? original : i));
      showErrorToast(describeWriteFailure(err, 'mark that invoice paid'));
    } finally {
      setMarkingId(null);
    }
  }

  async function handleSaveExpense(data) {
    const payload = {
      title: data.title,
      amount: data.amount,
      date: data.date,
      category: data.category,
      projectId: projectId,
      contactId: data.contact_id ?? null,
      receiptUrl: data.receipt_url ?? null,
      notes: data.notes ?? '',
      isBillable: data.is_billable ?? false,
      isBilled: data.is_billed ?? false,
      taxRate: data.tax_rate ?? 0,
    };

    // ── "EXPENSE SAVED." WAS SHOWN BEFORE THE REQUEST WAS EVEN SENT (2026-10-04) ──
    //
    // The toast fired, the row appeared, the form closed — and THEN an un-awaited api()
    // call went out whose only failure handling was console.error plus refreshExpenses().
    // With no network that refresh fails too, so the invented row stayed on screen under a
    // "saved" toast. This is money on a project: an expense that was never recorded,
    // looking recorded, is a number she would later invoice a client from.
    //
    // The optimistic row is KEPT, because a snappy list is worth having — but it is rolled
    // back on failure, the toast waits for the server, and the FORM STAYS OPEN so what she
    // typed is not lost. Nothing is claimed before it is true.
    const snapshot = expenses;
    if (data.id) {
      setExpenses(prev => prev.map(e => e.id === data.id ? { ...e, ...data } : e));
      try {
        await api(`/api/expenses/${data.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        showSuccessToast("Expense updated.");
      } catch (err) {
        setExpenses(snapshot);
        showErrorToast(describeWriteFailure(err, 'update that expense'));
        return;                       // the form stays open, holding her figures
      }
    } else {
      const tempId = Date.now();
      const optimistic = {
        ...data, id: tempId, project_id: projectId, created_at: new Date().toISOString(),
      };
      setExpenses(prev => [optimistic, ...prev]);
      try {
        const res = await api('/api/expenses', { method: 'POST', body: JSON.stringify(payload) });
        const d = await res.json().catch(() => ({}));
        // A 200 carrying no expense is a FAILURE: the row would keep a temporary id that
        // belongs to nothing, and editing it later would write to an id the server has
        // never heard of.
        if (!d.expense) throw new Error('The server did not return the saved expense.');
        setExpenses(prev => prev.map(e => e.id === tempId ? { ...optimistic, id: Number(d.expense.id) } : e));
        showSuccessToast("Expense saved.");
      } catch (err) {
        setExpenses(snapshot);
        showErrorToast(describeWriteFailure(err, 'save that expense'));
        return;
      }
    }
    setShowExpenseForm(false);
    setEditExpense(null);
  }

  // Decision 7: no browser confirm. The expense goes at once behind an Undo toast; the DELETE
  // is sent only when the Undo window closes, and a failed DELETE brings the expense back.
  function handleDeleteExpense(exp) {
    del.remove(exp);
  }

  // ── Guard ─────────────────────────────────────────────────────────────────

  const backButton = onBack ? <Button variant="secondary" onClick={onBack}>← Back</Button> : null;

  if (!project) {
    if (loading) return <Skeleton lines={4} label="Loading project financials" />;
    if (loadError) {
      return (
        <div className="pf">
          <SectionHead title="Project financials" action={backButton} />
          <ErrorState title="This project didn't load" message={messageFromError(loadError)} onRetry={loadProject} />
        </div>
      );
    }
    return (
      <div className="pf">
        <SectionHead title="Project financials" action={backButton} />
        <EmptyState icon="📁" title="Project not found" action={backButton}>
          It may have been deleted, or it belongs to another workspace.
        </EmptyState>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const hasBudget = Number(project.budget) > 0;
  const budget = Number(project.budget) || 0;
  const overBudget = hasBudget && financials.totalExpenses > budget;
  const budgetPct = hasBudget ? Math.min(100, (financials.totalExpenses / budget) * 100) : 0;
  const otherReceived = moneyBy(financials.otherReceived, base);
  const hasOtherReceived = Object.keys(financials.otherReceived).length > 0;
  const liveCount = invoices.filter(isInvoiced).length;

  return (
    <div className="pf">
      <SectionHead
        title={project.name}
        subline={<>
          <ProjectStatus status={project.status} />
          {project.client && <span>Client: <strong>{project.client}</strong></span>}
          {(project.start_date || project.end_date) && (
            <span>
              {project.start_date && `From ${fmtDate(project.start_date)}`}
              {project.start_date && project.end_date && " · "}
              {project.end_date && `To ${fmtDate(project.end_date)}`}
            </span>
          )}
        </>}
        action={<>
          {backButton}
          {onNewInvoice && (
            <Button variant="create" onClick={() => onNewInvoice(projectId)}>+ New invoice</Button>
          )}
        </>}
      />

      {/* ── SUMMARY ── */}
      <section className="invc-kpis" aria-label="Project totals">
        <StatTile card color="pastel-blue" label="Total invoiced" value={moneyBy(financials.invoicedBy, base)}
          hint={`${liveCount} sent ${liveCount === 1 ? 'invoice' : 'invoices'}, not drafts or cancelled`} />
        <StatTile card color="pastel-mint" label="Total received" value={moneyBy(financials.receivedBy, base)}
          hint="Paid invoices, less refunds" />
        <StatTile card color="pastel-yellow" label="Outstanding" value={moneyBy(financials.outstandingBy, base)}
          hint="Sent and not yet paid" />
        <StatTile card color="pastel-peach" label="Total expenses" value={formatMoney(financials.totalExpenses, base)}
          hint="All project expenses" />
        <StatTile card color={financials.netProfit >= 0 ? 'pastel-green' : 'pastel-coral'} label="Net profit"
          value={formatMoney(financials.netProfit, base)}
          hint={hasOtherReceived
            ? `Received minus expenses, in ${base}. ${otherReceived} received in other currencies is not included.`
            : 'Received minus expenses'} />
        {hasBudget && (
          <StatTile card color={overBudget ? 'pastel-coral' : 'pastel-lilac'} label="Budget remaining"
            value={formatMoney(budget - financials.totalExpenses, base)}
            hint={overBudget
              ? `${formatMoney(financials.totalExpenses - budget, base)} over budget`
              : `${budgetPct.toFixed(0)}% of budget used`} />
        )}
      </section>
      {hasBudget && (
        <ProgressBar label="Budget used by expenses" value={budgetPct} max={100}
          color={overBudget ? 'pastel-coral' : undefined}
          valueText={`${formatMoney(financials.totalExpenses, base)} of ${formatMoney(budget, base)}`} />
      )}

      {/* ── INVOICES ── */}
      <section className="pf-block" aria-labelledby="pf-invoices">
        <h3 id="pf-invoices" className="pf-block__title">Invoices</h3>

        {!onNewInvoice && (
          <p className="invc-muted">
            To create an invoice for this project, go to <strong>Invoicing</strong> and select this project in the invoice form.
          </p>
        )}

        {invoices.length === 0 ? (
          <EmptyState icon="🧾" title="No invoices yet"
            action={onNewInvoice ? <Button variant="create" onClick={() => onNewInvoice(projectId)}>+ New invoice</Button> : null}>
            No invoices attached to this project yet.
          </EmptyState>
        ) : (
          <div className="invc-table-wrap">
            <table className="invc-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Date issued</th>
                  <th>Due date</th>
                  <th>Status</th>
                  <th className="is-num">Amount</th>
                  <th><span className="ui-visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const total = calcInvoiceTotal(inv.lineItems);
                  const canMarkPaid = !isPaidStatus(inv.status) && inv.status !== 'draft';
                  return (
                    <tr key={inv.id}>
                      <td className="pf-strong">{inv.number}</td>
                      <td className="pf-nowrap">{fmtDate(inv.issueDate)}</td>
                      <td className="pf-nowrap">{fmtDate(inv.dueDate)}</td>
                      <td><InvoiceStatus invoice={inv} /></td>
                      <td className="is-num invc-money">{formatMoney(total, currencyOf(inv))}</td>
                      <td className="pf-actions">
                        {canMarkPaid && (
                          <Button variant="secondary" size="sm" onClick={() => handleMarkPaid(inv)}
                            disabled={markingId === inv.id} aria-busy={markingId === inv.id || undefined}>
                            Mark paid
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>Total</td>
                  <td className="is-num invc-money">{moneyBy(financials.invoicedBy, base)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {/* ── EXPENSES ── */}
      <section className="pf-block" aria-labelledby="pf-expenses">
        <div className="pf-block__head">
          <h3 id="pf-expenses" className="pf-block__title">Expenses</h3>
          <Button variant="create" onClick={() => { setEditExpense(null); setShowExpenseForm(true); }}>
            + New expense
          </Button>
        </div>

        {expenses.length === 0 ? (
          <EmptyState icon="💳" title="No expenses yet">
            No expenses attached to this project yet.
          </EmptyState>
        ) : (
          <div className="invc-table-wrap">
            <table className="invc-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th className="is-num">Amount</th>
                  <th>Billable</th>
                  <th>Billed</th>
                  <th><span className="ui-visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(exp => (
                  <tr key={exp.id}>
                    <td className="pf-nowrap">{fmtDate(exp.date)}</td>
                    <td className="pf-strong pf-title">{exp.title}</td>
                    <td>{exp.category ? <span className="invc-pill">{exp.category}</span> : <span className="invc-small">—</span>}</td>
                    <td className="is-num invc-money">{formatMoney(exp.amount, base)}</td>
                    <td>{exp.is_billable ? <StatusPill tone="pending">Billable</StatusPill> : <span className="invc-small">—</span>}</td>
                    <td>{exp.is_billed ? <StatusPill tone="success">Billed</StatusPill> : <span className="invc-small">—</span>}</td>
                    <td className="pf-actions">
                      <Button variant="secondary" size="sm" onClick={() => { setEditExpense(exp); setShowExpenseForm(true); }}>
                        Edit
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteExpense(exp)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  <td className="is-num invc-money">{formatMoney(financials.totalExpenses, base)}</td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {/* ── TIMELINE ── */}
      <section className="pf-block" aria-labelledby="pf-timeline">
        <h3 id="pf-timeline" className="pf-block__title">Timeline</h3>
        <Card lift={false}>
          <ProjectTimeline events={timelineEvents} />
        </Card>
      </section>

      {/* ── EXPENSE FORM MODAL ── */}
      {showExpenseForm && (
        <ExpenseForm
          expense={editExpense ?? { project_id: projectId }}
          onSave={handleSaveExpense}
          onClose={() => { setShowExpenseForm(false); setEditExpense(null); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmCancelled}
        onClose={() => setConfirmCancelled(null)}
        onConfirm={() => markPaid(confirmCancelled)}
        destructive={false}
        title="Mark a cancelled invoice as paid?"
        message={`Invoice ${confirmCancelled?.number || ''} is currently cancelled. Marking it paid un-voids it and records it as paid.`}
        confirmLabel="Mark as paid"
      />
    </div>
  );
}
